import { formatDkk } from '@jywer/ui';
import type { MonthlySnapshot } from './types';

/**
 * Simple inline SVG line chart for 12-month MRR trend. Rolls up multiple
 * subscription_types per snapshot_month into a single total. No chart
 * library dependency — the team doesn't need zoom/tooltip interactivity
 * in V1, a static trend line is sufficient for D.6's purpose.
 */
export function MrrLineChart({ rows }: { rows: MonthlySnapshot[] }) {
  const totals = new Map<string, number>();
  for (const r of rows) {
    const key = r.snapshot_month;
    totals.set(key, (totals.get(key) ?? 0) + Number(r.mrr));
  }

  const months = Array.from(totals.keys()).sort();
  if (months.length < 2) {
    return (
      <p className="text-sm text-ink-2">
        For få snapshots til en trend. Dashboardet udfyldes efterhånden som månedscron kører.
      </p>
    );
  }

  const values = months.map((m) => totals.get(m) ?? 0);
  const max = Math.max(...values, 1);
  const min = 0;
  const width = 600;
  const height = 160;
  const padX = 32;
  const padY = 16;
  const plotW = width - padX * 2;
  const plotH = height - padY * 2;

  const pointsArr = values.map((v, i) => {
    const x = padX + (i / Math.max(months.length - 1, 1)) * plotW;
    const y = padY + plotH - ((v - min) / (max - min || 1)) * plotH;
    return { x, y, v, month: months[i]! };
  });
  const polyline = pointsArr.map((p) => `${p.x},${p.y}`).join(' ');

  const gridLines = 4;
  const labels = Array.from({ length: gridLines + 1 }, (_, i) => {
    const v = min + ((max - min) / gridLines) * i;
    const y = padY + plotH - (i / gridLines) * plotH;
    return { y, label: formatDkk(v) };
  });

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full text-[var(--line-2)]"
      role="img"
      aria-label="MRR over 12 måneder"
    >
      {labels.map((l) => (
        <g key={l.y}>
          <line
            x1={padX}
            x2={width - padX}
            y1={l.y}
            y2={l.y}
            stroke="currentColor"
            strokeWidth={0.5}
          />
          <text x={4} y={l.y + 3} className="fill-[var(--ink-2)]" style={{ fontSize: '10px' }}>
            {l.label}
          </text>
        </g>
      ))}
      <polyline
        points={polyline}
        fill="none"
        stroke="#1A3D5C"
        strokeWidth={2}
        className="drop-shadow-sm"
      />
      {pointsArr.map((p) => (
        <g key={p.month}>
          <circle cx={p.x} cy={p.y} r={3} fill="#1A3D5C" />
          <text
            x={p.x}
            y={height - 2}
            textAnchor="middle"
            className="fill-[var(--ink-2)]"
            style={{ fontSize: '10px' }}
          >
            {p.month}
          </text>
        </g>
      ))}
    </svg>
  );
}
